import React from "react";
import { FileCheck, FileText, Plus, Trash2, UploadCloud, X } from "lucide-react";
import { storageUrl } from "./OlevelSittings";

/**
 * Academic qualifications — institution, qualification, year and the
 * certificate — captured the way the probationer application asks for them
 * (see application/components/steps/Step2Qualifications).
 *
 * These are the same records. What is entered here is written back onto the
 * member's probationer application, which is where the Institute keeps a
 * member's academic history, so the two screens are two doors into one record
 * rather than two lists that happen to look alike.
 */

export type QualificationRow = {
  institution: string;
  qualification: string;
  year: string;
  /** A certificate already on file — echoed back so saving does not detach it. */
  cert_file?: string | null;
  /** A newly chosen certificate, not yet uploaded. */
  file?: File | null;
};

/** Matches the server's cap, so an oversized file is caught before the upload. */
const MAX_FILE_MB = 10;

export const blankQualification = (): QualificationRow => ({
  institution: "",
  qualification: "",
  year: "",
  cert_file: null,
  file: null,
});

/**
 * The first thing wrong with these qualifications, or null.
 *
 * A certificate is required for each, as it is on the probationer form — the
 * record is what later applications pre-fill from, and a qualification with no
 * evidence behind it is not worth carrying forward.
 */
export function validateQualifications(rows: QualificationRow[]): string | null {
  if (rows.length === 0) return "Add at least one academic qualification.";

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const n = i + 1;

    if (!r.institution.trim()) return `Qualification ${n}: enter the institution.`;
    if (!r.qualification.trim()) return `Qualification ${n}: enter the qualification.`;
    if (!/^\d{4}$/.test(r.year.trim())) return `Qualification ${n}: enter the year as four digits.`;
    if (!r.file && !r.cert_file) return `Qualification ${n}: upload the certificate.`;
  }

  return null;
}

export default function QualificationRows({
  rows, onChange, disabled, onError,
}: {
  rows: QualificationRow[];
  onChange: (next: QualificationRow[]) => void;
  disabled?: boolean;
  onError?: (message: string) => void;
}) {
  const setRow = (i: number, patch: Partial<QualificationRow>) =>
    onChange(rows.map((r, x) => (x === i ? { ...r, ...patch } : r)));

  const pickFile = (i: number, file: File | null) => {
    if (file && file.size > MAX_FILE_MB * 1024 * 1024) {
      onError?.(
        `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The maximum is ${MAX_FILE_MB} MB — please upload a smaller or compressed file.`,
      );
      return;
    }
    setRow(i, { file });
  };

  return (
    <div className="space-y-4">
      {rows.map((r, i) => (
        <div key={i} className="border border-gray-200 rounded-2xl p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <span className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-[10px] font-bold uppercase tracking-widest">
              Qualification {i + 1}
            </span>
            {rows.length > 1 && !disabled && (
              <button
                type="button"
                onClick={() => onChange(rows.filter((_, x) => x !== i))}
                className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                title="Remove this qualification"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="grid sm:grid-cols-12 gap-3">
            <div className="sm:col-span-5">
              <Field
                label="Institution"
                value={r.institution}
                disabled={disabled}
                placeholder="University of Lagos"
                onChange={(v) => setRow(i, { institution: v })}
              />
            </div>
            <div className="sm:col-span-4">
              <Field
                label="Qualification"
                value={r.qualification}
                disabled={disabled}
                placeholder="B.Sc. Quantity Surveying"
                onChange={(v) => setRow(i, { qualification: v })}
              />
            </div>
            <div className="sm:col-span-3">
              <Field
                label="Year graduated"
                value={r.year}
                disabled={disabled}
                placeholder="2018"
                inputMode="numeric"
                maxLength={4}
                onChange={(v) => setRow(i, { year: v.replace(/\D/g, "").slice(0, 4) })}
              />
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-50 space-y-2">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">
              Certificate <span className="text-rose-500">*</span>
            </label>

            <div className="flex flex-wrap gap-2">
              {r.cert_file && !r.file && (
                <a
                  href={storageUrl(r.cert_file) ?? "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl text-xs font-medium hover:bg-emerald-100"
                >
                  <FileCheck className="w-4 h-4" /> On file
                </a>
              )}
              {r.file && (
                <span className="inline-flex items-center gap-2 px-3 py-2 bg-sky-50 text-sky-700 border border-sky-100 rounded-xl text-xs font-medium">
                  <FileText className="w-4 h-4" />
                  <span className="max-w-[10rem] truncate">{r.file.name}</span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => setRow(i, { file: null })}
                      className="p-0.5 hover:bg-sky-100 rounded"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </span>
              )}
            </div>

            {!disabled && (
              <label className="relative block cursor-pointer">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={(e) => {
                    pickFile(i, e.target.files?.[0] ?? null);
                    e.target.value = "";
                  }}
                />
                <span className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-200 rounded-xl text-xs font-bold text-gray-500 hover:border-gray-400 transition-all">
                  <UploadCloud className="w-4 h-4" />
                  {r.file || r.cert_file ? "Replace certificate" : "Upload certificate"}
                </span>
              </label>
            )}

            <p className="text-[10px] text-gray-400">PDF, JPG or PNG · up to {MAX_FILE_MB} MB</p>
          </div>
        </div>
      ))}

      {!disabled && (
        <button
          type="button"
          onClick={() => onChange([...rows, blankQualification()])}
          className="flex items-center gap-2 px-5 py-2.5 text-gray-900 border-2 border-dashed border-gray-300 rounded-2xl text-xs font-bold hover:border-gray-900 transition-all"
        >
          <Plus className="w-4 h-4" /> Add another qualification
        </button>
      )}
    </div>
  );
}

function Field({
  label, value, onChange, disabled, placeholder, inputMode, maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  inputMode?: "numeric";
  maxLength?: number;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">{label}</label>
      <input
        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
